import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faEnvelope, faPencil } from '@fortawesome/free-solid-svg-icons';
import { Tile, ContentErrorBlock } from './Tile';
import { PersonInfo } from './PersonInfo';
import { ContentType, shouldShowContent, EXPECTED_OWNING_PATIENT_ERRORS } from './contentTypes';
import { LmcContractViolationEnum } from '../../enums/lmcContractViolationEnum';
import { DocumentService, ConfigService, sharedApiClient } from '../../fetcher';
import { useAuth, useNotifications, CustomDropdown, CustomDatePicker, codePrimaryTerm, codesFromConfigFetchResponse, buildCodeDropdownOptions } from '@medprompt/shared';
import { UserAbility } from '@medprompt/shared';
import { FeatureFlags } from '../../FeatureFlags';

// Matches com.medprompt.document.details.DocumentTypeField (JSON uses enum name)
const DOCUMENT_TYPE_FIELD_DEFAULT = 'CLINICAL_DEPARTMENT';
const DOCUMENT_TYPE_FIELDS = ['CLINICAL_DEPARTMENT', 'LETTER_TYPE'];
const DOCUMENT_TYPE_FIELD_LABELS = {
  CLINICAL_DEPARTMENT: 'Clinical department',
  LETTER_TYPE: 'Letter type',
};

const DetailsTab = ({ doc, patientData, patientDataError, isRegenerationEnabled, isEditingEnabled, handleRegenerationClick, onDocumentUpdate }) => {
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const hasDocsWriteAbility = user?.authorities?.includes(UserAbility.DOCS_WRITE.key);
  const canEdit = isEditingEnabled ? isEditingEnabled() : hasDocsWriteAbility;

  // State for brief summary inline editing
  const [isEditingSummary, setIsEditingSummary] = useState(false);
  const [editableSummary, setEditableSummary] = useState('');
  const summaryTextareaRef = useRef(null);
  
  // State for NHS ID inline editing
  const [isEditingNhsId, setIsEditingNhsId] = useState(false);
  const [editableNhsId, setEditableNhsId] = useState('');
  const [nhsIdError, setNhsIdError] = useState('');
  const nhsIdInputRef = useRef(null);
  
  const documentManager = useMemo(() => new DocumentService(sharedApiClient), []);
  const configManager = useMemo(() => new ConfigService(sharedApiClient), []);

  const [clinicalDepartments, setClinicalDepartments] = useState([]);
  const [clinicalDeptFetchState, setClinicalDeptFetchState] = useState('idle');
  const [clinicalDepartmentLocal, setClinicalDepartmentLocal] = useState(null);
  const [clinicalDeptDropdownOpen, setClinicalDeptDropdownOpen] = useState(false);

  const [letterTypes, setLetterTypes] = useState([]);
  const [letterTypeFetchState, setLetterTypeFetchState] = useState('idle');
  const [letterTypeLocal, setLetterTypeLocal] = useState(null);
  const [letterTypeDropdownOpen, setLetterTypeDropdownOpen] = useState(false);

  // State for document details date editing
  const [localDate, setLocalDate] = useState(null);

  const [isHiddenFromPatientLocal, setIsHiddenFromPatientLocal] = useState(null);

  // State for sender field editing
  const [isEditingSenderField, setIsEditingSenderField] = useState({});
  const [editedSender, setEditedSender] = useState({});
  const senderFieldRefs = useRef({});

  // State for recipient field editing
  const [isEditingRecipientField, setIsEditingRecipientField] = useState({});
  const [editedRecipient, setEditedRecipient] = useState({});
  const recipientFieldRefs = useRef({});

  const clinicalDepartmentOptions = useMemo(() =>
    buildCodeDropdownOptions(clinicalDepartments).map((opt) =>
      String(opt.code).startsWith('CUSTOM')
        ? { ...opt, icon: faPencil, iconClassName: 'dropdown-option-icon--freetext' }
        : opt
    ), [clinicalDepartments]);

  const letterTypeOptions = useMemo(() =>
    buildCodeDropdownOptions(letterTypes).map((opt) =>
      String(opt.code).startsWith('CUSTOM')
        ? { ...opt, icon: faPencil, iconClassName: 'dropdown-option-icon--freetext' }
        : opt
    ), [letterTypes]);

  const activeDocumentTypeField = doc?.documentDetails?.documentTypeField ?? DOCUMENT_TYPE_FIELD_DEFAULT;

  const resolvedDocumentTypeCode = useMemo(() => {
    if (activeDocumentTypeField === 'LETTER_TYPE') {
      return letterTypeLocal ?? doc?.documentDetails?.letterTypeCode;
    }
    return clinicalDepartmentLocal ?? doc?.documentDetails?.clinicalDepartment;
  }, [
    activeDocumentTypeField,
    letterTypeLocal,
    clinicalDepartmentLocal,
    doc?.documentDetails?.letterTypeCode,
    doc?.documentDetails?.clinicalDepartment,
  ]);

  // These fields only lived in local input state, so a later refresh of `doc` reverted them.
  // Writing the saved value back into the parent keeps what the user sees and what the
  // controller holds in step; the update event still reconciles against the server.
  //
  // Both merge into the current document rather than one captured at render: these run once the
  // save resolves, by which point the update stream may already have replaced `doc`.
  //
  // Saves carry no abort signal, so a slow one can still resolve after Prev/Next has moved on.
  // The id check keeps a stale field from being written onto a different document.
  const targetDocumentId = doc?.documentId;

  const patchDoc = useCallback((changes) => {
    onDocumentUpdate?.(prevDoc => (prevDoc?.documentId === targetDocumentId
      ? { ...prevDoc, ...changes }
      : prevDoc));
  }, [onDocumentUpdate, targetDocumentId]);

  const patchDocumentDetails = useCallback((changes) => {
    onDocumentUpdate?.(prevDoc => (prevDoc?.documentId === targetDocumentId
      ? { ...prevDoc, documentDetails: { ...(prevDoc.documentDetails || {}), ...changes } }
      : prevDoc));
  }, [onDocumentUpdate, targetDocumentId]);

  const isHiddenFromPatient = isHiddenFromPatientLocal ?? doc?.documentDetails?.isHiddenFromPatient ?? false;
  const isPatientFacingVisible = !isHiddenFromPatient;

  const handlePatientFacingVisibilityChange = useCallback((checked) => {
    if (!canEdit) return;
    const nextIsHiddenFromPatient = !checked;
    setIsHiddenFromPatientLocal(nextIsHiddenFromPatient);
    documentManager.updateDocumentDetails(doc.documentId, { isHiddenFromPatient: nextIsHiddenFromPatient })
      .then(() => patchDocumentDetails({ isHiddenFromPatient: nextIsHiddenFromPatient }))
      .catch((error) => {
        console.error('Error updating patient facing visibility:', error);
        setIsHiddenFromPatientLocal(null);
        addNotification(`patient-facing-visibility-error-${doc.documentId}`, 'Failed to update patient facing visibility', { isError: true });
      });
  }, [canEdit, doc?.documentId, documentManager, addNotification, patchDocumentDetails]);

  const handleDocumentTypeFieldChange = useCallback((field) => {
    if (!canEdit || field === activeDocumentTypeField) return;
    documentManager.updateDocumentDetails(doc.documentId, { documentTypeField: field })
      .then(() => patchDocumentDetails({ documentTypeField: field }))
      .catch((error) => {
        console.error('Error updating document type field:', error);
        addNotification(`document-type-field-error-${doc.documentId}`, 'Failed to update document type', { isError: true });
      });
  }, [canEdit, activeDocumentTypeField, doc?.documentId, documentManager, addNotification, patchDocumentDetails]);

  useEffect(() => {
    setClinicalDepartmentLocal(null);
    setClinicalDeptDropdownOpen(false);
    setLetterTypeLocal(null);
    setLetterTypeDropdownOpen(false);
    setLocalDate(null);
    setIsHiddenFromPatientLocal(null);
    setEditedSender({});
    setEditedRecipient({});
    setIsEditingSenderField({});
    setIsEditingRecipientField({});
  }, [doc?.documentId]);

  useEffect(() => {
    let cancelled = false;
    setClinicalDeptFetchState('loading');
    configManager
      .fetchClinicalDepartmentsForDocument()
      .then((data) => {
        if (cancelled) return;
        setClinicalDepartments(codesFromConfigFetchResponse(data));
        setClinicalDeptFetchState('loaded');
      })
      .catch((err) => {
        console.error('Error loading clinical departments:', err);
        if (!cancelled) {
          setClinicalDepartments([]);
          setClinicalDeptFetchState('error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [configManager]);

  useEffect(() => {
    let cancelled = false;
    setLetterTypeFetchState('loading');
    configManager
      .fetchLetterTypesForDocument()
      .then((data) => {
        if (cancelled) return;
        setLetterTypes(codesFromConfigFetchResponse(data));
        setLetterTypeFetchState('loaded');
      })
      .catch((err) => {
        console.error('Error loading letter types:', err);
        if (!cancelled) {
          setLetterTypes([]);
          setLetterTypeFetchState('error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [configManager]);

  // Update editable values when doc changes (but only when not editing)
  useEffect(() => {
    if (!isEditingSummary) {
      setEditableSummary(doc?.briefSummary || '');
    }
  }, [doc?.briefSummary, isEditingSummary]);

  useEffect(() => {
    if (!isEditingNhsId) {
      setEditableNhsId(doc?.nhsId || '');
    }
  }, [doc?.nhsId, isEditingNhsId]);

  // Focus inputs when editing starts
  useEffect(() => {
    if (isEditingSummary && summaryTextareaRef.current) {
      summaryTextareaRef.current.focus();
    }
  }, [isEditingSummary]);

  useEffect(() => {
    if (isEditingNhsId && nhsIdInputRef.current) {
      nhsIdInputRef.current.focus();
      nhsIdInputRef.current.select();
    }
  }, [isEditingNhsId]);

  // Copy to clipboard function
  const handleCopyToClipboard = useCallback((text) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => {
        addNotification(`copy-success-${Date.now()}`, 'Copied to clipboard', { isError: false });
      }).catch(() => {
        addNotification(`copy-error-${Date.now()}`, 'Failed to copy to clipboard', { isError: true });
      });
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        addNotification(`copy-success-${Date.now()}`, 'Copied to clipboard', { isError: false });
      } catch (err) {
        addNotification(`copy-error-${Date.now()}`, 'Failed to copy to clipboard', { isError: true });
      }
      document.body.removeChild(textArea);
    }
  }, [addNotification]);

  // Summary editing handlers
  const handleSummaryClick = useCallback(() => {
    if (!canEdit) return;
    setIsEditingSummary(true);
  }, [canEdit]);

  const handleSummaryBlur = useCallback(() => {
    setIsEditingSummary(false);
    // Auto-save if there are changes
    if (editableSummary !== (doc?.briefSummary || '')) {
      documentManager.updateDocumentSummary(doc.documentId, editableSummary)
        .then(() => patchDoc({ briefSummary: editableSummary }))
        .catch(error => {
          console.error('Error updating document summary:', error);
          addNotification(`summary-update-error-${doc.documentId}`, 'Failed to update document summary', { isError: true });
        });
    }
  }, [editableSummary, doc?.briefSummary, doc?.documentId, documentManager, addNotification, patchDoc]);

  const handleSummaryKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      setEditableSummary(doc?.briefSummary || '');
      setIsEditingSummary(false);
    }
  }, [doc?.briefSummary]);

  // NHS ID editing handlers
  const handleNhsIdClick = useCallback(() => {
    if (!canEdit) return;
    setIsEditingNhsId(true);
    setNhsIdError('');
  }, [canEdit]);

  const handleNhsIdBlur = useCallback(() => {
    if (!editableNhsId.trim()) {
      // If empty, just close without saving
      setIsEditingNhsId(false);
      setEditableNhsId(doc?.nhsId || '');
      setNhsIdError('');
      return;
    }

    const nhsIdRegex = /^\d{3}[ -]?\d{3}[ -]?\d{4}$/;
    
    if (!nhsIdRegex.test(editableNhsId)) {
      setNhsIdError('NHS ID must be in format: 123 456 7890');
      return;
    }
    
    const cleanNhsId = editableNhsId.replace(/[\s-]/g, '');
    setNhsIdError('');
    setIsEditingNhsId(false);
    
    // Auto-save if changed
    if (cleanNhsId !== doc?.nhsId) {
      documentManager.updateNhsId(doc.documentId, cleanNhsId)
        .then(() => patchDoc({ nhsId: cleanNhsId }))
        .catch((error) => {
          console.error('Error updating NHS ID:', error);
          addNotification(`nhs-id-error-${doc.documentId}`, 'Failed to update NHS ID', { isError: true });
          // Revert on error
          setEditableNhsId(doc?.nhsId || '');
        });
    }
  }, [editableNhsId, doc?.nhsId, doc?.documentId, documentManager, addNotification, patchDoc]);

  const handleNhsIdKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      setEditableNhsId(doc?.nhsId || '');
      setIsEditingNhsId(false);
      setNhsIdError('');
    } else if (e.key === 'Enter') {
      e.preventDefault();
      nhsIdInputRef.current?.blur();
    }
  }, [doc?.nhsId]);

  // Date editing handlers
  const handleDateChange = useCallback((newDate) => {
    setLocalDate(newDate);
    documentManager.updateDocumentDetails(doc.documentId, { date: newDate })
      .then(() => patchDocumentDetails({ date: newDate }))
      .catch(error => {
        console.error('Error updating document date:', error);
        addNotification(`date-update-error-${doc.documentId}`, 'Failed to update document date', { isError: true });
      });
  }, [doc?.documentId, documentManager, addNotification, patchDocumentDetails]);

  // Person field editing handlers
  const handlePersonFieldClick = useCallback((personType, fieldName) => {
    if (!canEdit) return;
    if (personType === 'sender') {
      setIsEditingSenderField(prev => ({ ...prev, [fieldName]: true }));
    } else {
      setIsEditingRecipientField(prev => ({ ...prev, [fieldName]: true }));
    }
  }, [canEdit]);

  const handlePersonFieldBlur = useCallback((personType, fieldName) => {
    if (personType === 'sender') {
      setIsEditingSenderField(prev => ({ ...prev, [fieldName]: false }));
      const currentValue = doc?.documentDetails?.sender?.[fieldName] || '';
      const newValue = editedSender[fieldName];
      if (newValue !== undefined && newValue !== currentValue) {
        const updatedSender = { ...doc.documentDetails.sender, [fieldName]: newValue };
        documentManager.updateDocumentDetails(doc.documentId, { sender: updatedSender })
          .then(() => patchDocumentDetails({ sender: updatedSender }))
          .catch(error => {
            console.error('Error updating sender:', error);
            addNotification(`sender-update-error-${doc.documentId}`, 'Failed to update sender', { isError: true });
          });
      }
    } else {
      setIsEditingRecipientField(prev => ({ ...prev, [fieldName]: false }));
      const currentValue = doc?.documentDetails?.recipient?.[fieldName] || '';
      const newValue = editedRecipient[fieldName];
      if (newValue !== undefined && newValue !== currentValue) {
        const updatedRecipient = { ...doc.documentDetails.recipient, [fieldName]: newValue };
        documentManager.updateDocumentDetails(doc.documentId, { recipient: updatedRecipient })
          .then(() => patchDocumentDetails({ recipient: updatedRecipient }))
          .catch(error => {
            console.error('Error updating recipient:', error);
            addNotification(`recipient-update-error-${doc.documentId}`, 'Failed to update recipient', { isError: true });
          });
      }
    }
  }, [doc?.documentDetails, doc?.documentId, editedSender, editedRecipient, documentManager, addNotification, patchDocumentDetails]);

  const handlePersonFieldChange = useCallback((personType, fieldName, value) => {
    if (personType === 'sender') {
      setEditedSender(prev => ({ ...prev, [fieldName]: value }));
    } else {
      setEditedRecipient(prev => ({ ...prev, [fieldName]: value }));
    }
  }, []);

  const handlePersonFieldKeyDown = useCallback((e, personType, fieldName) => {
    if (e.key === 'Escape') {
      if (personType === 'sender') {
        setEditedSender(prev => ({ ...prev, [fieldName]: doc?.documentDetails?.sender?.[fieldName] || '' }));
        setIsEditingSenderField(prev => ({ ...prev, [fieldName]: false }));
      } else {
        setEditedRecipient(prev => ({ ...prev, [fieldName]: doc?.documentDetails?.recipient?.[fieldName] || '' }));
        setIsEditingRecipientField(prev => ({ ...prev, [fieldName]: false }));
      }
    } else if (e.key === 'Enter' && fieldName !== 'address') {
      e.preventDefault();
      const ref = personType === 'sender' ? senderFieldRefs.current[fieldName] : recipientFieldRefs.current[fieldName];
      ref?.blur();
    }
  }, [doc?.documentDetails]);

  // Focus handlers for person fields
  useEffect(() => {
    Object.keys(isEditingSenderField).forEach(fieldName => {
      if (isEditingSenderField[fieldName] && senderFieldRefs.current[fieldName]) {
        senderFieldRefs.current[fieldName].focus();
      }
    });
  }, [isEditingSenderField]);

  useEffect(() => {
    Object.keys(isEditingRecipientField).forEach(fieldName => {
      if (isEditingRecipientField[fieldName] && recipientFieldRefs.current[fieldName]) {
        recipientFieldRefs.current[fieldName].focus();
      }
    });
  }, [isEditingRecipientField]);

  // Extract NHS ID content
  const renderNhsIdContent = () => {
    const formattedNhsId = editableNhsId && editableNhsId.length === 10 
      ? `${editableNhsId.slice(0, 3)} ${editableNhsId.slice(3, 6)} ${editableNhsId.slice(6)}`
      : editableNhsId;

    return (
      <div className="patient-details detected-info">
        <div className="document-detail-row nhs-number-detail-row">
          <span className="document-detail-label nhs-number-detail-label">NHS Number:</span>
          <div className="detail-value-with-controls">
            {isEditingNhsId ? (
              <div className="nhs-id-inline-edit-container">
                <input
                  ref={nhsIdInputRef}
                  type="text"
                  className={`nhs-id-inline-input ${nhsIdError ? 'nhs-input-error' : ''}`}
                  value={editableNhsId}
                  onChange={(e) => setEditableNhsId(e.target.value)}
                  onBlur={handleNhsIdBlur}
                  onKeyDown={handleNhsIdKeyDown}
                  placeholder="XXX XXX XXXX"
                />
                {nhsIdError && <div className="nhs-input-error-message">{nhsIdError}</div>}
              </div>
            ) : (
              <>
                <span 
                  className={`document-detail-value nhs-number-detail-value ${canEdit ? 'detail-value-editable' : ''}`}
                  onClick={handleNhsIdClick}
                  onMouseMove={(e) => {
                    const tooltip = e.currentTarget.querySelector('.edit-tooltip');
                    if (tooltip) {
                      tooltip.style.left = `${e.clientX + 10}px`;
                      tooltip.style.top = `${e.clientY + 10}px`;
                    }
                  }}
                >
                  {formattedNhsId || <em>not found</em>}
                  {canEdit && <div className="edit-tooltip">Click to edit NHS Number</div>}
                </span>
                {editableNhsId && (
                  <button 
                    className="button copy-button" 
                    onClick={() => handleCopyToClipboard(editableNhsId)}
                    title="Copy NHS number"
                  >
                    <FontAwesomeIcon icon={faCopy} />
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Extract patient info content (name + address)
  const renderPatientInfoContent = () => {
    // TOP-LEVEL DECISION: Does doc.patientId exist?
    if (!doc?.patientId) {
      // NO patientId - Check document status
      if (doc?.status === 'GENERATING') {
        // Document is GENERATING and no patientId yet - show spinning animation
        // This will be handled by ConditionalContent returning LoadingSection
        return null; // ConditionalContent will show LoadingSection
      } else {
        // Document is READY (or other status) but no patientId - patient not matched
        return (
          <div className="patient-details detected-info">
            <div className="empty-state-container">
              <div className="empty-state-message">NHS number not matched</div>
            </div>
          </div>
        );
      }
    }

    // YES patientId exists - Check if data loaded or error occurred
    if (patientDataError) {
      // Fetch failed
      return (
        <div className="patient-details detected-info">
          <div className="empty-state-container">
            <div className="patient-data-error-pill">
              Patient data failed to load
            </div>
          </div>
        </div>
      );
    }

    if (!patientData) {
      // Fetch is pending (patientId exists but data not yet loaded)
      return (
        <div className="patient-details detected-info">
          <div className="data-loading-container">
            <div className="data-loading-spinner"></div>
            <div className="data-loading-text">Loading patient data...</div>
          </div>
        </div>
      );
    }

    // Helper to calculate age
    const calculateAge = (dob) => {
      if (!dob) return null;
      try {
        const [day, month, year] = dob.split('/').map(Number);
        const birthDate = new Date(year, month - 1, day);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        return age >= 0 ? `${age}y` : null;
      } catch {
        return null;
      }
    };

    // patientData exists - show actual patient details
    return (
      <div className="patient-details detected-info">
        <div className="document-detail-row">
          <span className="document-detail-label">Name:</span>
          <span className="document-detail-value">{patientData?.name || <em>not found</em>}</span>
        </div>
        <div className="document-detail-row">
          <span className="document-detail-label">Age:</span>
          <span className="document-detail-value">{calculateAge(patientData?.dateOfBirth) || <em>not found</em>}</span>
        </div>
        <div className="document-detail-row">
          <span className="document-detail-label">Sex:</span>
          <span className="document-detail-value">{patientData?.sex || <em>not found</em>}</span>
        </div>
      </div>
    );
  };

  // Render editable person field
  const renderEditablePersonField = (personType, fieldName, label, isTextarea = false) => {
    const person = personType === 'sender' ? doc?.documentDetails?.sender : doc?.documentDetails?.recipient;
    const isEditingMap = personType === 'sender' ? isEditingSenderField : isEditingRecipientField;
    const editedMap = personType === 'sender' ? editedSender : editedRecipient;
    const refsMap = personType === 'sender' ? senderFieldRefs : recipientFieldRefs;
    
    const isEditing = isEditingMap[fieldName];
    const currentValue = person?.[fieldName] || '';
    const editValue = editedMap[fieldName] !== undefined ? editedMap[fieldName] : currentValue;

    if (!canEdit) {
      // Read-only display - vertical stack
      if (fieldName === 'address' && currentValue) {
        return (
          <div className="info-row info-row--stacked">
            <span className="label">{label}</span>
            <span className="value">
              {currentValue.split(', ').map((line, index) => (
                <div key={index} className="address-line">{line}</div>
              ))}
            </span>
          </div>
        );
      }
      return currentValue ? (
        <div className="info-row info-row--stacked">
          <span className="label">{label}</span>
          <span className="value">{currentValue}</span>
        </div>
      ) : null;
    }

    // Editable display - always vertical stack
    return (
      <div className="info-row info-row--stacked">
        <span className="label">{label}</span>
        {isEditing ? (
          isTextarea ? (
            <textarea
              ref={el => refsMap.current[fieldName] = el}
              value={editValue}
              onChange={(e) => handlePersonFieldChange(personType, fieldName, e.target.value)}
              onBlur={() => handlePersonFieldBlur(personType, fieldName)}
              onKeyDown={(e) => handlePersonFieldKeyDown(e, personType, fieldName)}
              className="person-field-edit-textarea"
              rows="3"
            />
          ) : (
            <input
              ref={el => refsMap.current[fieldName] = el}
              type="text"
              value={editValue}
              onChange={(e) => handlePersonFieldChange(personType, fieldName, e.target.value)}
              onBlur={() => handlePersonFieldBlur(personType, fieldName)}
              onKeyDown={(e) => handlePersonFieldKeyDown(e, personType, fieldName)}
              className="person-field-edit-input"
            />
          )
        ) : (
          <div
            className="person-field-editable"
            onClick={() => handlePersonFieldClick(personType, fieldName)}
            onMouseMove={(e) => {
              const tooltip = e.currentTarget.querySelector('.edit-tooltip');
              if (tooltip) {
                tooltip.style.left = `${e.clientX + 10}px`;
                tooltip.style.top = `${e.clientY + 10}px`;
              }
            }}
          >
            {currentValue ? (
              fieldName === 'address' ? (
                currentValue.split(', ').map((line, index) => (
                  <div key={index} className="address-line">{line}</div>
                ))
              ) : (
                currentValue
              )
            ) : (
              <em style={{ color: '#999' }}>Not set</em>
            )}
            <div className="edit-tooltip">Click to edit</div>
          </div>
        )}
      </div>
    );
  };

  // Extract document details content
  const renderDocumentDetails = () => (
    <div className="details-section">
      {FeatureFlags.PATIENT_FACING_VISIBILITY_TOGGLE && (
        <div className="info-row patient-facing-visibility-row">
          <label className="fileaway-checkbox-label">
            <input
              type="checkbox"
              checked={isPatientFacingVisible}
              onChange={(e) => handlePatientFacingVisibilityChange(e.target.checked)}
              disabled={!canEdit}
            />
            <span>Display on patient's online record</span>
          </label>
        </div>
      )}
      <div className="info-row info-row--stacked document-type-field-row">
        <span className="label">Document type on EHR system</span>
        <div className="document-type-field-control">
          <div className="details-document-type-toggle-shell">
            <div
              className="details-document-type-toggle"
              role="group"
              aria-label="Document type source"
            >
              {DOCUMENT_TYPE_FIELDS.map((field) => (
                <button
                  key={field}
                  type="button"
                  className={`details-document-type-toggle-btn ${activeDocumentTypeField === field ? 'is-selected' : ''}`}
                  onClick={() => handleDocumentTypeFieldChange(field)}
                  disabled={!canEdit}
                >
                  {DOCUMENT_TYPE_FIELD_LABELS[field]}
                </button>
              ))}
            </div>
          </div>
          {codePrimaryTerm(resolvedDocumentTypeCode) ? (
            <div className="document-type-resolved-value">
              {codePrimaryTerm(resolvedDocumentTypeCode)}
            </div>
          ) : (
            <div className="document-type-resolved-value document-type-resolved-value--empty">
              <em>No {DOCUMENT_TYPE_FIELD_LABELS[activeDocumentTypeField].toLowerCase()} selected</em>
            </div>
          )}
        </div>
      </div>
      <ContentErrorBlock
        doc={doc}
        contentType={ContentType.LETTER_TYPE_STATUS}
        isRetryEnabled={isRegenerationEnabled()}
        onRegenerateClick={handleRegenerationClick}
        compact
      />
      <div className={`info-row info-row--stacked letter-type-row ${activeDocumentTypeField === 'LETTER_TYPE' ? 'document-type-source-active' : 'document-type-source-inactive'}`}>
        <span className="label">Letter type</span>
        <div className="letter-type-control">
          {canEdit ? (
            letterTypeFetchState === 'loading' ? (
              <em style={{ color: '#888' }}>Loading letter types…</em>
            ) : letterTypeFetchState === 'error' ? (
              <em style={{ color: '#888' }}>Could not load letter types</em>
            ) : (
              <CustomDropdown
                key={`${doc.documentId}-letter-type`}
                searchable
                allowFreeText
                selected={codePrimaryTerm(
                  letterTypeLocal ?? doc.documentDetails?.letterTypeCode
                )}
                searchPlaceholder="Search letter types…"
                options={letterTypeOptions}
                isOpen={letterTypeDropdownOpen}
                toggleDropdown={() => setLetterTypeDropdownOpen((o) => !o)}
                onSelect={(option) => {
                  setLetterTypeLocal(option);
                  setLetterTypeDropdownOpen(false);
                  documentManager.updateDocumentDetails(doc.documentId, { letterTypeCode: option })
                    .then(() => patchDocumentDetails({ letterTypeCode: option }))
                    .catch(() => {});
                }}
                keyExtractor={(option) => option.code}
                disabled={letterTypeFetchState !== 'loaded'}
                className="letter-type-dropdown"
              />
            )
          ) : codePrimaryTerm(doc.documentDetails?.letterTypeCode) ? (
            codePrimaryTerm(doc.documentDetails?.letterTypeCode)
          ) : (
            <em style={{ color: '#888' }}>Not set</em>
          )}
        </div>
      </div>
      <ContentErrorBlock
        doc={doc}
        contentType={ContentType.CLINICAL_DEPARTMENT_STATUS}
        isRetryEnabled={isRegenerationEnabled()}
        onRegenerateClick={handleRegenerationClick}
        compact
      />
      <div className={`info-row info-row--stacked clinical-department-row ${activeDocumentTypeField === 'CLINICAL_DEPARTMENT' ? 'document-type-source-active' : 'document-type-source-inactive'}`}>
        <span className="label">Clinical department</span>
        <div className="clinical-department-control">
          {canEdit ? (
            clinicalDeptFetchState === 'loading' ? (
              <em style={{ color: '#888' }}>Loading departments…</em>
            ) : clinicalDeptFetchState === 'error' ? (
              <em style={{ color: '#888' }}>Could not load departments</em>
            ) : (
              <CustomDropdown
                key={`${doc.documentId}-clinical-dept`}
                searchable
                allowFreeText
                selected={codePrimaryTerm(
                  clinicalDepartmentLocal ?? doc.documentDetails?.clinicalDepartment
                )}
                searchPlaceholder="Search departments…"
                options={clinicalDepartmentOptions}
                isOpen={clinicalDeptDropdownOpen}
                toggleDropdown={() => setClinicalDeptDropdownOpen((o) => !o)}
                onSelect={(option) => {
                  setClinicalDepartmentLocal(option);
                  setClinicalDeptDropdownOpen(false);
                  documentManager.updateDocumentDetails(doc.documentId, { clinicalDepartment: option })
                    .then(() => patchDocumentDetails({ clinicalDepartment: option }))
                    .catch(() => {});
                }}
                keyExtractor={(option) => option.code}
                disabled={clinicalDeptFetchState !== 'loaded'}
                className="clinical-department-dropdown"
              />
            )
          ) : codePrimaryTerm(doc.documentDetails?.clinicalDepartment) ? (
            codePrimaryTerm(doc.documentDetails?.clinicalDepartment)
          ) : (
            <em style={{ color: '#888' }}>Not set</em>
          )}
        </div>
      </div>
      <div className="info-row info-row--stacked date-row">
        <span className="label">Date</span>
        <div className="date-control">
          {canEdit ? (
            <CustomDatePicker
              value={localDate ?? doc?.documentDetails?.date}
              onChange={handleDateChange}
            />
          ) : (
            <span className="value">{doc?.documentDetails?.date || <em style={{ color: '#888' }}>Not set</em>}</span>
          )}
        </div>
      </div>
      {(canEdit || doc?.documentDetails?.sender) && (
        <details open>
          <summary>
            Sender
            {doc?.senderOdsCode && (
              <span className="ods-badge" title="Verified from NHS ODS">✓ NHS MESH Verified</span>
            )}
            {doc?.senderEmail && (
              <span className="email-badge">
                <FontAwesomeIcon icon={faEnvelope} className="email-badge-icon" aria-hidden />
                Email Verified
              </span>
            )}
          </summary>
          <div className="document-person-info">
            {renderEditablePersonField('sender', 'name', 'Name')}
            {renderEditablePersonField('sender', 'organisation', 'Organisation')}
            {renderEditablePersonField('sender', 'email', 'Email')}
            {renderEditablePersonField('sender', 'specialty', 'Specialty')}
            {doc?.senderOdsCode && (
              <div className="info-row info-row--stacked">
                <span className="label">ODS Code</span>
                <span className="value">{doc.senderOdsCode}</span>
              </div>
            )}
            {renderEditablePersonField('sender', 'address', 'Address', true)}
            {renderEditablePersonField('sender', 'phoneNumber', 'Phone Number')}
          </div>
        </details>
      )}
      {(canEdit || doc?.documentDetails?.recipient) && (
        <details open>
          <summary>Recipient</summary>
          <div className="document-person-info">
            {renderEditablePersonField('recipient', 'name', 'Name')}
            {renderEditablePersonField('recipient', 'organisation', 'Organisation')}
            {renderEditablePersonField('recipient', 'email', 'Email')}
            {renderEditablePersonField('recipient', 'address', 'Address', true)}
            {renderEditablePersonField('recipient', 'phoneNumber', 'Phone Number')}
          </div>
        </details>
      )}
    </div>
  );

  // Extract brief summary content
  const renderSummaryContent = () => (
    <>
    <ContentErrorBlock
      doc={doc}
      contentType={ContentType.SUMMARY_STATUS}
      isRetryEnabled={isRegenerationEnabled()}
      onRegenerateClick={handleRegenerationClick}
    />
    <div className="document-ai-item">
      {isEditingSummary ? (
        <textarea
          ref={summaryTextareaRef}
          value={editableSummary}
          onChange={(e) => setEditableSummary(e.target.value)}
          onBlur={handleSummaryBlur}
          onKeyDown={handleSummaryKeyDown}
          className="summary-edit-textarea"
          rows="10"
        />
      ) : (
        <div 
          className={`summary-text ${canEdit ? 'summary-text-editable' : ''}`}
          onClick={handleSummaryClick}
          onMouseMove={(e) => {
            const tooltip = e.currentTarget.querySelector('.edit-tooltip');
            if (tooltip) {
              tooltip.style.left = `${e.clientX + 10}px`;
              tooltip.style.top = `${e.clientY + 10}px`;
            }
          }}
        >
          {editableSummary || <em style={{ color: '#999' }}>No summary available</em>}
          {canEdit && <div className="edit-tooltip">Click to edit summary</div>}
        </div>
      )}
    </div>
    </>
  );

  // Extract contract violations content
  const renderContractViolations = () => {
    const violations = doc?.lmcContractViolations || [];
    
    if (violations.length === 0) {
      return <div className="empty-contract-violations-section">No contract violations found</div>;
    }

    return (
      <div className="contract-violations-section">
        {violations.map((violationType, index) => {
          const violation = LmcContractViolationEnum[violationType];
          if (!violation) return null;
          
          return (
            <details key={violationType} className="violation-details" open={index === 0}>
              <summary className="violation-summary">
                <span className="violation-name">{violation.displayName}</span>
              </summary>
              <div className="violation-description">
                {violation.description}
              </div>
            </details>
          );
        })}
      </div>
    );
  };

  return (
    <div className="document-detail-tiles">
      {doc?.isMultiplePatients === true && (
        <div className="multiple-patients-section">
          <div className="multiple-patients-header">
            <span className="multiple-patients-label">Multiple patients</span>
            <span className="multiple-patients-count">1</span>
          </div>
          <div className="multiple-patients-warning">
            This upload may contain documents for more than one patient.
          </div>
        </div>
      )}
      <Tile 
        doc={doc}
        documentId={doc?.documentId} 
        contentType={ContentType.NHS_ID_STATUS} 
        title="NHS Number"
        condition={shouldShowContent(doc?.nhsId, doc?.status)}
        isEnabled={isRegenerationEnabled()} 
        onRegenerateClick={handleRegenerationClick}
        showContentOnError
        shouldShowError={() => !(typeof doc?.nhsId === 'string' && doc.nhsId.trim().length > 0)}
      >
        {renderNhsIdContent()}
      </Tile>

      <Tile 
        doc={doc}
        documentId={doc?.documentId} 
        contentType={ContentType.OWNING_PATIENT_STATUS} 
        title="Patient Details"
        condition={doc?.patientId || doc?.status !== 'GENERATING'}
        isEnabled={isRegenerationEnabled()} 
        onRegenerateClick={handleRegenerationClick}
        showContentOnError
        shouldShowError={(error) => !doc?.patientId && !EXPECTED_OWNING_PATIENT_ERRORS.has(error.message)}
      >
        {renderPatientInfoContent()}
      </Tile>

      <Tile
        doc={doc}
        documentId={doc?.documentId} 
        contentType={ContentType.BRIEF_SUMMARY_STATUS} 
        title="Summary"
        condition={shouldShowContent(doc?.briefSummary, doc?.status)}
        isEnabled={isRegenerationEnabled()} 
        onRegenerateClick={handleRegenerationClick}
        showContentOnError
      >
        {renderSummaryContent()}
      </Tile>

      <Tile
        className="contract-violations"
        doc={doc}
        documentId={doc?.documentId}
        contentType={ContentType.LMC_CONTRACT_VIOLATION_STATUS}
        title="Transfer of Care Violations"
        condition={shouldShowContent(doc?.lmcContractViolations, doc?.status)}
        isEnabled={isRegenerationEnabled()}
        onRegenerateClick={handleRegenerationClick}
        shouldShowError={() => !(doc?.lmcContractViolations?.length > 0)}
        badgePredicate={doc?.lmcContractViolations && doc.lmcContractViolations.length > 0}
        badgeValue={doc?.lmcContractViolations?.length}
      >
        {renderContractViolations()}
      </Tile>

      <Tile
        doc={doc}
        documentId={doc?.documentId}
        contentType={ContentType.DOCUMENT_DETAILS_STATUS}
        title="Document Details"
        condition={shouldShowContent(doc?.documentDetails, doc?.status)}
        isEnabled={isRegenerationEnabled()}
        onRegenerateClick={handleRegenerationClick}
        showContentOnError
      >
        {doc?.documentDetails ? renderDocumentDetails() : <div className="document-ai-item">No document details available</div>}
      </Tile>
    </div>
  );
};

export default DetailsTab; 